/**
 * shadcn-style <Badge> — minimal hand-roll.
 *
 * A small chip for kinds/labels (one shape app-wide, rounded-chip — Q-CHIP).
 * Status chips with per-value colour maps stay as bespoke `cn()`-composed
 * spans — this Badge is for the
 * simpler "just label it" cases. CVA-based, same pattern as
 * `button.tsx`.
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-chip border px-2 py-0.5 text-xs font-medium transition-colors",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        outline: "text-foreground",
        muted: "border-transparent bg-muted text-muted-foreground",
        amber: "border-warning-border bg-warning-bg text-warning",
        blue: "border-info-border bg-info-bg text-interactive",
        emerald: "border-success-border bg-success-bg text-success",
        slate: "border-transparent bg-muted text-muted-foreground",
        red: "border-error-border bg-error-bg text-error",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
